import { Collection } from 'firestorter';

import Collections from '../utils/collections';
import BaseDocument from './BaseDocument';

// Firestore caps an 'in' filter at 10 values.
const IN_QUERY_LIMIT = 10;

// A workout synced from the client's health app: duration in seconds, distance in miles.
class TrackedActivity extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.TRACKED_ACTIVITY}/${id}`, opts);
  }

  get user() {
    return this.data.user;
  }

  get activityType() {
    return this.data.activityType;
  }

  get startDate() {
    return this.data.startDate?.toDate();
  }

  get endDate() {
    return this.data.endDate?.toDate();
  }

  get duration() {
    return this.data.duration || 0;
  }

  get distance() {
    return this.data.distance || 0;
  }

  get activeCalories() {
    return this.data.activeCalories || 0;
  }

  get basalEnergyBurned() {
    return this.data.basalEnergyBurned || 0;
  }

  get minHeartRate() {
    return this.data.minHeartRate;
  }

  get maxHeartRate() {
    return this.data.maxHeartRate;
  }

  get averageHeartRate() {
    return this.data.averageHeartRate;
  }

  // Per-minute buckets, oldest first.
  get heartRateDataPoints() {
    return (this.data.heartRateDataPoints || [])
      .map(({ date, minHeartRate, maxHeartRate }) => ({ date: date?.toDate?.() ?? null, minHeartRate, maxHeartRate }))
      .filter(({ date }) => !!date)
      .sort((a, b) => a.date - b.date);
  }

  get sourceName() {
    return this.data.sourceName;
  }

  get customTitle() {
    return this.data.customTitle;
  }

  get gameplaySession() {
    return this.data.gameplaySession;
  }

  static async getById(id) {
    const trackedActivity = new TrackedActivity(id);
    await trackedActivity.init();
    return trackedActivity.exists ? trackedActivity : null;
  }

  // The client's tracked activities recorded during the given gameplay sessions.
  static async getByGameplaySessions(userId, gameplaySessionIds) {
    const chunks = [];
    for (let i = 0; i < gameplaySessionIds.length; i += IN_QUERY_LIMIT) {
      chunks.push(gameplaySessionIds.slice(i, i + IN_QUERY_LIMIT));
    }
    const collections = await Promise.all(chunks.map(async (chunk) => {
      const collection = new Collection(Collections.TRACKED_ACTIVITY, {
        createDocument: ({ id }, opts) => new TrackedActivity(id, opts),
        query: (ref) => ref
          .where('user', '==', userId)
          .where('gameplaySession', 'in', chunk),
      });
      await TrackedActivity.initCollection(collection);
      return collection;
    }));
    return collections.flatMap((collection) => collection.docs);
  }
}

export default TrackedActivity;
